import { ChangeEvent, useRef, useState } from 'react';

import styled from 'styled-components';

import { Avatar } from '@components/avatar';
import { IconEdit } from '@components/icons/edit';
import { IconImage } from '@components/icons/image';
import { IconRemove } from '@components/icons/remove';
import { IconUpload } from '@components/icons/upload';
import { SizedRelativeSpinner } from '@components/loading-spinner';
import { Stack } from '@components/stack';
import { VisuallyHidden } from '@components/visually-hidden';
import { theme } from '@styles/theme';
import { getBase64 } from '@utils/browser';
import { transitionStandard } from '@utils/transitions';

const VALID_PICTURE_TYPES = ['image/jpeg', 'image/png'];
export const PROFILE_IMAGE_SIZES = ['small', 'large'] as const;
export type ProfileImageSize = (typeof PROFILE_IMAGE_SIZES)[number];

export type AddProfileImageProps = {
  name?: string;
  initalPicture?: string;
  picture?: File | null;
  setPicture: (picture: File | null) => void;
  clearPicture?: boolean;
  setClearPicture: (clearPicture: boolean) => void;
  setIsPictureInvalid?: (isInvalid: boolean) => void;
  size?: ProfileImageSize;
};

export const AddProfileImage = ({
  name,
  initalPicture,
  picture,
  setPicture,
  clearPicture,
  setClearPicture,
  setIsPictureInvalid,
  size = 'small',
}: AddProfileImageProps) => {
  const isLarge = size === 'large';
  const [pictureBase64, setPictureBase64] = useState('');
  const [isReadingFile, setIsReadingFile] = useState(false);

  async function changePicture(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target?.files?.[0];
    if (!file) return;

    setPicture(file);
    setClearPicture(false);
    setIsPictureInvalid?.(!VALID_PICTURE_TYPES.includes(file.type));

    setIsReadingFile(true);
    try {
      setPictureBase64(await getBase64(file));
    } catch (error) {
      setPictureBase64('');
      setPicture(null);
      setIsPictureInvalid?.(true);
    } finally {
      setIsReadingFile(false);
    }
  }

  const removePicture = () => {
    setPictureBase64('');
    setClearPicture(true);
    setIsPictureInvalid?.(false);
    setPicture(null);
  };

  return (
    <ProfilePicContainer size={size}>
      <ProfilePicItem isOver={true}>
        <EditCircle
          hasImage={Boolean(picture) || (Boolean(initalPicture) && !clearPicture)}
          changePicture={changePicture}
          removePicture={removePicture}
          isReading={isReadingFile}
          size={size}
        />
      </ProfilePicItem>
      <ProfilePicItem>
        {!name && !picture ? (
          <EmptyProfilePic size={size} />
        ) : (
          <Avatar
            size={size === 'large' ? 'xxxl' : 'xxl'}
            name={name ?? ''}
            src={pictureBase64 || (!clearPicture ? initalPicture : '')}
          />
        )}
      </ProfilePicItem>
      {isReadingFile && (
        <ProfilePicItem isOver={true}>
          <BackgroundCircle size={size}>
            <SizedRelativeSpinner
              size={isLarge ? 48 : 24}
              $skipMargin
              $borderWidth={isLarge ? 5 : 3}
              $color="white100"
            />
          </BackgroundCircle>
        </ProfilePicItem>
      )}
    </ProfilePicContainer>
  );
};

const EmptyProfilePic = ({ size }: { size: ProfileImageSize }) => {
  return (
    <BackgroundCircle background={true} size={size}>
      <IconImage color="uiTrans50" size={32} />
    </BackgroundCircle>
  );
};

type EditCircleProps = {
  hasImage: boolean;
  changePicture: (event: ChangeEvent<HTMLInputElement>) => void;
  removePicture: () => void;
  isReading: boolean;
  size: ProfileImageSize;
};
const EditCircle = ({
  hasImage,
  changePicture,
  removePicture,
  isReading,
  size,
}: EditCircleProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const clickUpload = () => {
    fileInputRef?.current?.click();
  };

  const handleRemove = () => {
    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
    removePicture();
  };

  return (
    <BackgroundCircle id="background-circle" size={size}>
      {hasImage ? (
        <Stack space={2} orientation="horizontal">
          <IconEdit color="white100" onClick={clickUpload} style={{ cursor: 'pointer' }} />

          <IconRemove
            color="white100"
            onClick={handleRemove}
            style={{ cursor: 'pointer' }}
            dataTestid="remove-profile-picture"
          />
        </Stack>
      ) : (
        <IconUpload color="white100" onClick={clickUpload} style={{ cursor: 'pointer' }} />
      )}
      <VisuallyHidden>
        <input
          ref={fileInputRef}
          name="file"
          type="file"
          accept="image/*"
          onChange={changePicture}
          disabled={isReading}
        />
      </VisuallyHidden>
    </BackgroundCircle>
  );
};

const BackgroundCircle = styled.div<{ size: ProfileImageSize; background?: boolean }>((p) => ({
  height: p.size === 'large' ? '8rem' : '4rem',
  width: p.size === 'large' ? '8rem' : '4rem',
  backgroundColor: p.background ? theme.color.uiTrans5 : theme.color.purpleTrans80,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: transitionStandard('opacity'),
}));

const ProfilePicContainer = styled.div<{ size: ProfileImageSize }>((p) => ({
  width: p.size === 'large' ? '8rem' : '4rem',
  height: p.size === 'large' ? '8rem' : '4rem',
  position: 'relative',
  '&:not(:hover) #background-circle': {
    opacity: 0,
  },
  '&:hover #background-circle': {
    opacity: 1,
  },
}));

const ProfilePicItem = styled.div<{ isOver?: boolean }>((p) => ({
  position: 'absolute',
  zIndex: p.isOver ? 2 : 1,
}));
